import { motion } from "framer-motion";
import {
  Home, CloudSun, LayoutDashboard, MapPin, Calculator, Wrench,
  Camera, Sparkles, Repeat, Package, ClipboardList, CalendarRange, Flame,
  DatabaseBackup, Archive,
} from "lucide-react";
import { FEATURE_LIST } from "@/lib/landing";

const Beaker = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4.5 3h15" />
    <path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3" />
    <path d="M6 14h12" />
  </svg>
);

const Trophy = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const ShieldCheck = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const Sprout = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M7 20h10" />
    <path d="M10 20c5.5-2.5.8-6.4 3-10" />
    <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" />
    <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" />
  </svg>
);

const TreePine = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7Z" />
    <path d="M12 22v-3" />
  </svg>
);

const ICONS = {
  Home, CloudSun, LayoutDashboard, MapPin, Calculator, Wrench,
  Camera, Sparkles, Repeat, Package, ClipboardList, CalendarRange, Flame,
  DatabaseBackup, Archive, Beaker, Trophy, ShieldCheck, Sprout, TreePine,
};

export default function Features() {
  return (
    <section id="features" className="relative z-10 mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16 max-w-3xl"
      >
        <span className="text-xs font-black uppercase tracking-[0.25em] text-[var(--ld-lime)]">
          The Instrument Panel
        </span>
        <h2 className="ld-display mt-4 text-[clamp(2rem,5vw,3.75rem)]">
          Every tool for a better lawn.
        </h2>
        <p className="mt-4 text-lg text-[var(--ld-muted)]">
          Everything you need to run your program — tap any tool to see it in action.
        </p>
      </motion.div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURE_LIST.map((f, i) => {
          const Icon = ICONS[f.icon];
          return (
            <motion.a
              key={f.id}
              href={`#feat-${f.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}
              data-testid={`feature-card-${f.id}`}
              className="group rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/50 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--ld-lime)]/40 hover:bg-[var(--ld-surface)]"
            >
              <div className="mb-4 grid h-12 w-12 place-items-center rounded-xl bg-[var(--ld-lime)]/12 text-[var(--ld-lime)] transition-colors duration-300 group-hover:bg-[var(--ld-lime)] group-hover:text-[var(--ld-bg)]">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-black text-[var(--ld-text)]">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[var(--ld-muted)]">{f.body}</p>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
}
